/**
 * RdvManager — gestion des rendez-vous (mairie / GRC).
 *
 * Trois volets pour l'écran « Services & vie quotidienne » :
 *   · Motifs : créer/éditer/supprimer les types de RDV (nom, durée, lieu, actif).
 *   · Disponibilités : plages d'ouverture récurrentes par motif (jour + heures).
 *   · Rendez-vous pris : liste à venir, marquer honoré / annuler.
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}).
 * Alerte visuelle si un motif actif n'a plus de créneau (cohérent avec le cron).
 */

import { useEffect, useState, useCallback } from 'react';
import { Button, Badge, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { RdvMotif, RdvDisponibilite, RdvReservation, RdvEtatMairie } from '@/lib/api';
import {
  Plus, Pencil, Trash2, Clock, MapPin, CalendarClock, CheckCircle2, X, AlertCircle, AlertTriangle,
} from 'lucide-react';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function formatDatePure(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

export function RdvManager({ onEtatChange }: { onEtatChange?: () => void }) {
  const { confirm, dialogElement } = useConfirmDialog();

  const [onglet, setOnglet] = useState<'motifs' | 'rdv'>('motifs');
  const [etat, setEtat] = useState<RdvEtatMairie | null>(null);
  const [motifs, setMotifs] = useState<RdvMotif[]>([]);
  const [dispos, setDispos] = useState<RdvDisponibilite[]>([]);
  const [reservations, setReservations] = useState<RdvReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [motifEdit, setMotifEdit] = useState<RdvMotif | 'new' | null>(null);
  const [dispoFor, setDispoFor] = useState<RdvMotif | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [e, m, d, r] = await Promise.all([
        api.grc.rdv.etat(),
        api.grc.rdv.listMotifs(),
        api.grc.rdv.listDisponibilites(),
        api.grc.rdv.listReservations({ aVenir: true, statut: 'reserve' }),
      ]);
      setEtat(e);
      setMotifs(m.motifs);
      setDispos(d.disponibilites);
      setReservations(r.reservations);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function refreshAll() { await reload(); onEtatChange?.(); }

  async function supprimerMotif(m: RdvMotif) {
    const ok = await confirm({
      title: 'Supprimer ce motif ?',
      description: `« ${m.nom} », ses disponibilités et les rendez-vous associés seront supprimés.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.rdv.removeMotif(m.id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  async function marquerHonore(r: RdvReservation) {
    try { await api.grc.rdv.changerStatut(r.id, 'honore'); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Action impossible'); }
  }

  async function annulerRdv(r: RdvReservation) {
    const ok = await confirm({
      title: 'Annuler ce rendez-vous ?',
      description: `${r.motifNom} — ${formatDatePure(r.dateRdv)} à ${r.heureDebut}${r.nom ? ` (${r.nom})` : ''}.`,
      confirmLabel: 'Annuler le RDV', danger: true,
    });
    if (!ok) return;
    try { await api.grc.rdv.changerStatut(r.id, 'annule'); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Action impossible'); }
  }

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      {/* Alerte : motifs actifs sans créneau (même signal que le cron staff) */}
      {etat && etat.motifsSansCreneau.length > 0 && (
        <Alert variant="warning" icon={AlertTriangle} className="mb-3">
          Aucun créneau disponible pour&nbsp;: {etat.motifsSansCreneau.join(', ')}. Ajoutez des
          disponibilités, sinon les administrés ne peuvent plus réserver.
        </Alert>
      )}

      {/* Onglets internes */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setOnglet('motifs')}
            className={onglet === 'motifs'
              ? 'rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 py-1 text-xs font-medium text-grc-fg'
              : 'rounded-r-pill border border-transparent px-3 py-1 text-xs font-medium text-ink-muted hover:border-border hover:text-ink'}>
            Motifs &amp; disponibilités
          </button>
          <button type="button" onClick={() => setOnglet('rdv')}
            className={onglet === 'rdv'
              ? 'rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 py-1 text-xs font-medium text-grc-fg'
              : 'rounded-r-pill border border-transparent px-3 py-1 text-xs font-medium text-ink-muted hover:border-border hover:text-ink'}>
            Rendez-vous pris{reservations.length > 0 ? ` (${reservations.length})` : ''}
          </button>
        </div>
        {onglet === 'motifs' && (
          <Button variant="ghost" size="sm" onClick={() => setMotifEdit('new')}>
            <Plus className="h-4 w-4" /> Motif
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : onglet === 'motifs' ? (
        motifs.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Aucun motif. Créez-en un (ex. « CNI / Passeport ») puis ajoutez ses plages d'ouverture.
          </p>
        ) : (
          <ul className="space-y-2">
            {motifs.map((m) => {
              const md = dispos.filter((d) => d.motifId === m.id);
              return (
                <li key={m.id} id={`motif-${m.id}`} className="rounded-r-lg border border-border bg-surface/50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-medium text-ink">
                        {m.nom}
                        {m.actif ? <Badge variant="success" size="sm">Actif</Badge> : <Badge variant="neutral" size="sm">Inactif</Badge>}
                      </div>
                      {m.description && <p className="mt-0.5 text-sm text-ink-muted">{m.description}</p>}
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{m.dureeMin} min</span>
                        {m.lieu && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{m.lieu}</span>}
                      </div>
                      {/* Disponibilités */}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {md.length === 0 ? (
                          <span className="text-xs text-warning">Aucune plage — indisponible aux administrés</span>
                        ) : md.map((d) => (
                          <span key={d.id} className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">
                            {JOURS[d.jour]} {d.heureDebut}–{d.heureFin}
                            <button type="button" onClick={() => void supprimerDispo(d.id)} className="text-ink-dim hover:text-urgent" title="Retirer">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setDispoFor(m)} title="Ajouter une plage">
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setMotifEdit(m)} title="Modifier">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => void supprimerMotif(m)} title="Supprimer">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : (
        // Onglet RDV pris
        reservations.length === 0 ? (
          <p className="text-sm text-ink-muted">Aucun rendez-vous à venir.</p>
        ) : (
          <ul className="space-y-2">
            {reservations.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-ink">
                    <CalendarClock className="h-4 w-4 text-grc" aria-hidden="true" />
                    {r.motifNom}
                  </div>
                  <div className="mt-0.5 text-sm text-ink-muted">
                    {formatDatePure(r.dateRdv)} à {r.heureDebut}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                    {r.nom && <span>{r.nom}</span>}
                    {r.telephone && <span>{r.telephone}</span>}
                    {r.email && <span>{r.email}</span>}
                  </div>
                  {r.note && <div className="mt-0.5 text-xs italic text-ink-dim">{r.note}</div>}
                  {/* Item 13.2 — le numéro sert AU GUICHET : sans cet affichage,
                      la saisie côté administré n'aurait servi à rien. En gras,
                      parce que c'est ce que l'agent cherche des yeux. */}
                  {r.numeroPredemande && (
                    <div className="mt-0.5 text-xs text-ink-muted">
                      Pré-demande ANTS : <span className="font-medium text-ink">{r.numeroPredemande}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <Button variant="ghost" size="sm" onClick={() => void marquerHonore(r)} title="Marquer honoré">
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void annulerRdv(r)} title="Annuler">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )
      )}

      {/* Modales */}
      {motifEdit && (
        <MotifDialog
          initial={motifEdit === 'new' ? null : motifEdit}
          onClose={() => setMotifEdit(null)}
          onSaved={async () => { setMotifEdit(null); await refreshAll(); }}
        />
      )}
      {dispoFor && (
        <DispoDialog
          motif={dispoFor}
          onClose={() => setDispoFor(null)}
          onSaved={async () => { setDispoFor(null); await refreshAll(); }}
        />
      )}
      {dialogElement}
    </div>
  );

  async function supprimerDispo(id: string) {
    try { await api.grc.rdv.removeDisponibilite(id); await refreshAll(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }
}

// ============================================================================
// Modale — Motif
// ============================================================================

function MotifDialog({
  initial, onClose, onSaved,
}: {
  initial: RdvMotif | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [lieu, setLieu] = useState(initial?.lieu ?? '');
  const [dureeMin, setDureeMin] = useState(initial?.dureeMin ?? 20);
  const [actif, setActif] = useState(initial?.actif ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = { nom: nom.trim(), description: description.trim() || null, lieu: lieu.trim() || null, dureeMin, actif };
      if (initial) await api.grc.rdv.updateMotif(initial.id, body);
      else await api.grc.rdv.createMotif(body);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible');
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier le motif' : 'Nouveau motif de RDV'}>
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Nom du motif" htmlFor="rdv-nom" required>
          <Input id="rdv-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="CNI / Passeport, Rencontre avec le maire…" />
        </Field>
        <Field label="Description / consignes (optionnel)" htmlFor="rdv-desc">
          <textarea
            id="rdv-desc"
            className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Pièces à apporter, pré-demande ANTS conseillée…"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lieu (optionnel)" htmlFor="rdv-lieu">
            <Input id="rdv-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder="Mairie — guichet 2" />
          </Field>
          <Field label="Durée d'un créneau (min)" htmlFor="rdv-duree" required>
            <Input id="rdv-duree" type="number" min={5} max={240} step={5} value={dureeMin}
              onChange={(e) => setDureeMin(Number(e.target.value))} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
          Proposé aux administrés (actif)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !nom.trim()}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
// Modale — Disponibilité
// ============================================================================

function DispoDialog({
  motif, onClose, onSaved,
}: {
  motif: RdvMotif;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [jour, setJour] = useState(0);
  const [heureDebut, setHeureDebut] = useState('09:00');
  const [heureFin, setHeureFin] = useState('12:00');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (heureFin <= heureDebut) { setErr('L\'heure de fin doit être après le début.'); return; }
    setBusy(true); setErr(null);
    try {
      await api.grc.rdv.createDisponibilite({ motifId: motif.id, jour, heureDebut, heureFin });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible');
      setBusy(false);
    }
  }

  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  return (
    <Dialog open onClose={onClose} title={`Plage d'ouverture — ${motif.nom}`}>
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Jour" htmlFor="disp-jour" required>
          <select id="disp-jour" className={selectCls} value={jour} onChange={(e) => setJour(Number(e.target.value))}>
            {JOURS.map((j, i) => <option key={i} value={i}>{j}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="De" htmlFor="disp-debut" required>
            <Input id="disp-debut" type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} />
          </Field>
          <Field label="À" htmlFor="disp-fin" required>
            <Input id="disp-fin" type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} />
          </Field>
        </div>
        <p className="text-xs text-ink-dim">
          Le moteur découpe cette plage en créneaux de {motif.dureeMin} min.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>
            {busy ? 'Enregistrement…' : 'Ajouter'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
